import { useState, useEffect, useRef } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import athleteApi from '@/lib/api'
import { INPUT_CLASSES } from '@/lib/theme'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { PasswordInput } from '@/components/ui/password-input'
import { useForm } from 'react-hook-form'
import { ProfilePhotoUpload, userService } from '@adthlete/shared-frontend'
import { toast } from '@/components/ui/sonner'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { useDispatch } from 'react-redux'
import { sendPhoneCodeThunk, verifyPhoneCodeThunk, removePhoneThunk } from '@/redux/userSlice'
import { AppDispatch } from '@/store'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { Info } from 'lucide-react'
import { useSearchParams } from 'react-router-dom'
import { Switch } from '@/components/ui/switch'

const profileFormSchema = z
  .object({
    name: z.string().min(2, { message: 'Name must be at least 2 characters.' }),
    email: z.string().email({ message: 'Please enter a valid email.' }),
    currentPassword: z.string().optional(),
    newPassword: z.string().optional(),
    confirmPassword: z.string().optional(),
  })
  .refine(
    (data) => {
      // If any password field is filled, all must be filled
      const hasAnyPassword = data.currentPassword || data.newPassword || data.confirmPassword
      if (!hasAnyPassword) return true
      return data.currentPassword && data.newPassword && data.confirmPassword
    },
    {
      message: 'All password fields are required to change password',
      path: ['currentPassword'],
    },
  )
  .refine(
    (data) => {
      // If changing password, new password must be at least 6 characters
      if (!data.newPassword) return true
      return data.newPassword.length >= 6
    },
    {
      message: 'Password must be at least 6 characters',
      path: ['newPassword'],
    },
  )
  .refine(
    (data) => {
      // If changing password, passwords must match
      if (!data.newPassword || !data.confirmPassword) return true
      return data.newPassword === data.confirmPassword
    },
    {
      message: 'Passwords do not match',
      path: ['confirmPassword'],
    },
  )

interface AccountSettingsProps {
  athlete: {
    id?: string | number
    name?: string
    email?: string
    avatar?: string
    searchable?: boolean
    sports?: number[]
  }
  profileLoading: boolean
  updateProfile: (updates: {
    name?: string
    email?: string
    searchable?: boolean
  }) => Promise<void>
  updateAvatar: (avatarUrl: string) => Promise<void>
}

const AccountSettings = ({
  athlete,
  profileLoading,
  updateProfile,
  updateAvatar,
}: AccountSettingsProps) => {
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false)
  const [profilePhoto, setProfilePhoto] = useState<string | undefined>(athlete?.avatar)
  const [isUpdatingVisibility, setIsUpdatingVisibility] = useState(false)
  const formInitialized = useRef(false)

  const form = useForm<z.infer<typeof profileFormSchema>>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: {
      name: '',
      email: '',
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  })

  // Sync local loading states with Redux loading states
  useEffect(() => {
    if (profileLoading && !isUpdatingProfile) {
      setIsUpdatingProfile(true)
    } else if (!profileLoading && isUpdatingProfile) {
      setIsUpdatingProfile(false)
    }
  }, [profileLoading, isUpdatingProfile])

  // Update profile photo when athlete avatar changes
  useEffect(() => {
    if (athlete?.avatar) {
      setProfilePhoto(athlete.avatar)
    }
  }, [athlete?.avatar])

  // Initialize form values only once when athlete is first loaded
  useEffect(() => {
    if (athlete && !formInitialized.current && !isUpdatingProfile && !profileLoading) {
      form.reset({
        name: athlete.name || '',
        email: athlete.email || '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      })
      formInitialized.current = true
    }
  }, [athlete, form, isUpdatingProfile, profileLoading])

  const onSubmit = async (data: z.infer<typeof profileFormSchema>) => {
    // Build object of real changes
    const profileUpdates: { name?: string; email?: string } = {}
    if (data.name !== athlete?.name) profileUpdates.name = data.name
    if (data.email !== athlete?.email) profileUpdates.email = data.email

    const isChangingPassword = data.currentPassword && data.newPassword && data.confirmPassword

    // Exit early if nothing changed
    if (Object.keys(profileUpdates).length === 0 && !isChangingPassword) return

    setIsUpdatingProfile(true)
    try {
      // Update profile if there are changes
      if (Object.keys(profileUpdates).length > 0) {
        await updateProfile(profileUpdates)
      }

      // Handle password change if requested
      if (isChangingPassword) {
        try {
          await userService.updatePassword({
            currentPassword: data.currentPassword!,
            newPassword: data.newPassword!,
          })
          toast('Password updated', {
            description: 'Your password has been changed successfully.',
          })

          // Clear password fields after successful update
          form.setValue('currentPassword', '')
          form.setValue('newPassword', '')
          form.setValue('confirmPassword', '')
        } catch (error: any) {
          console.error('Password change error:', error)
          toast.error('Error', {
            description:
              error.response?.data?.error ||
              'Failed to update password. Please check your current password.',
          })
          // Don't clear fields on error so user can retry
          return
        }
      }

      toast('Profile updated', { description: 'Your profile information has been updated.' })
    } catch (error) {
      // Logic handled above for password, general catch for other updates
      if (!isChangingPassword) {
        toast.error('Error', { description: 'Failed to update profile. Please try again.' })
      }
    } finally {
      setIsUpdatingProfile(false)
    }
  }

  const handlePhotoChange = async (photo: string) => {
    try {
      setProfilePhoto(photo)
      // Call updateAvatar which will dispatch the thunk to update in Redux
      await updateAvatar(photo)
    } catch (error) {
      console.error('Failed to update avatar:', error)
      toast.error('Error', { description: 'Failed to update profile picture. Please try again.' })
    }
  }

  const handlePhotoRemove = async () => {
    try {
      const defaultAvatar = 'https://i.pravatar.cc/150?img=11'
      setProfilePhoto(defaultAvatar)
      await userService.updateAvatarUrl(defaultAvatar)
      await updateAvatar(defaultAvatar)
      toast('Photo removed', { description: 'Your profile photo has been removed.' })
    } catch (error) {
      console.error('Failed to remove avatar:', error)
      toast.error('Error', { description: 'Failed to remove profile picture. Please try again.' })
    }
  }

  const handleMarketplaceVisibilityChange = async (searchable: boolean) => {
    setIsUpdatingVisibility(true)
    try {
      await updateProfile({ searchable })
      toast(searchable ? 'Profile discoverable' : 'Profile hidden', {
        description: searchable
          ? 'Brands can discover your profile in the marketplace.'
          : 'Your profile is hidden from marketplace discovery. Existing collaborations are unchanged.',
      })
    } catch (error) {
      console.error('Failed to update marketplace visibility:', error)
      toast.error('Error', { description: 'Failed to update marketplace visibility.' })
    } finally {
      setIsUpdatingVisibility(false)
    }
  }

  return (
    <Card variant="dark" className="overflow-hidden">
      <CardHeader>
        <CardTitle className="text-white">Account Information</CardTitle>
        <CardDescription className="text-gray-400">
          Update your account details and preferences.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <ProfilePhotoUpload
              currentPhoto={profilePhoto}
              onPhotoChange={handlePhotoChange}
              onPhotoRemove={handlePhotoRemove}
              onSuccess={(msg) => toast('Success', { description: msg })}
              onError={(err) => toast.error('Error', { description: err.message })}
            />

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-white">Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Your name" {...field} className={INPUT_CLASSES.dark} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-white">Email</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder="Your email"
                      {...field}
                      className={INPUT_CLASSES.dark}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex items-start justify-between gap-4 rounded-xl border border-white/10 bg-white/5 p-4">
              <div className="space-y-1">
                <h3 className="font-medium text-white">Marketplace discoverability</h3>
                <p className="text-sm text-gray-400">
                  {athlete?.searchable === false
                    ? 'Your profile is hidden from Brand marketplace searches.'
                    : 'Allow Brands to discover your profile in marketplace searches.'}
                </p>
                <p className="text-xs text-gray-500">
                  This setting is separate from your public profile and does not affect existing
                  collaborations.
                </p>
              </div>
              <Switch
                aria-label="Allow Brands to discover my marketplace profile"
                checked={athlete?.searchable !== false}
                onCheckedChange={handleMarketplaceVisibilityChange}
                disabled={isUpdatingVisibility || profileLoading}
                className="data-[state=checked]:bg-[#C0FD72]"
              />
            </div>

            <div className="pt-4 border-t border-white/10">
              <h3 className="text-lg font-semibold text-white mb-4">Phone Verification</h3>
              <PhoneVerificationSection athlete={athlete} />
            </div>

            <div className="pt-4 border-t border-white/10">
              <h3 className="text-lg font-semibold text-white mb-4">Update Password</h3>

              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="currentPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-white">Current Password</FormLabel>
                      <FormControl>
                        <PasswordInput
                          placeholder="Enter current password"
                          {...field}
                          className={INPUT_CLASSES.dark}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="newPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-white">New Password</FormLabel>
                      <FormControl>
                        <PasswordInput
                          placeholder="Enter new password"
                          {...field}
                          className={INPUT_CLASSES.dark}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="confirmPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-white">Confirm New Password</FormLabel>
                      <FormControl>
                        <PasswordInput
                          placeholder="Confirm new password"
                          {...field}
                          className={INPUT_CLASSES.dark}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <Button
              type="submit"
              variant="lime"
              className="w-full"
              disabled={isUpdatingProfile || profileLoading}
            >
              {isUpdatingProfile || profileLoading ? 'Saving...' : 'Save Updates'}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  )
}

const PhoneVerificationSection = ({ athlete }: { athlete: any }) => {
  // Use local state for inputs since these are not part of the main profile form
  const [phone, setPhone] = useState(athlete.phone || '')
  const [code, setCode] = useState('')
  const [codeSent, setCodeSent] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const dispatch = useDispatch<AppDispatch>()
  const [searchParams] = useSearchParams()
  const phoneInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (searchParams.get('focus') === 'phone' && !athlete.phone_verified) {
      phoneInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      phoneInputRef.current?.focus()
    }
  }, [athlete.phone_verified, searchParams])

  // Update local phone state if athlete data changes (e.g. initial load)
  useEffect(() => {
    if (athlete.phone) {
      setPhone(athlete.phone)
    }
  }, [athlete.phone])

  const handleSendCode = async () => {
    if (!phone || phone.length < 10) {
      toast.error('Invalid phone number', { description: 'Please enter a valid phone number.' })
      return
    }

    setIsLoading(true)
    try {
      await dispatch(sendPhoneCodeThunk(phone)).unwrap()
      setCodeSent(true)
      toast('Code sent', { description: `Verification code sent to ${phone}` })
    } catch (error: any) {
      toast.error('Error', { description: error || 'Failed to send verification code.' })
    } finally {
      setIsLoading(false)
    }
  }

  const handleVerifyCode = async () => {
    if (!code || code.length < 6) {
      toast.error('Invalid code', { description: 'Please enter a 6-digit code.' })
      return
    }

    setIsLoading(true)
    try {
      await dispatch(verifyPhoneCodeThunk({ phone, code })).unwrap()
      setCodeSent(false)
      setCode('')
      toast('Success', { description: 'Phone number verified successfully!' })
    } catch (error: any) {
      toast.error('Verification failed', { description: error || 'Invalid code.' })
    } finally {
      setIsLoading(false)
    }
  }

  const handleRemovePhone = async () => {
    setIsLoading(true)
    try {
      await dispatch(removePhoneThunk()).unwrap()
      setPhone('')
      setCodeSent(false)
      setCode('')
      toast('Phone number removed', {
        description: 'Your phone number has been removed from your account.',
      })
    } catch (error: any) {
      toast.error('Error', { description: error || 'Failed to remove phone number.' })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col space-y-2">
        <div className="flex items-center gap-1.5">
          <FormLabel className="text-white">Phone Number</FormLabel>
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-3.5 w-3.5 text-gray-400 cursor-pointer hover:text-gray-200 transition-colors" />
              </TooltipTrigger>
              <TooltipContent
                side="right"
                className="max-w-xs bg-[#1a2a38] border-white/10 text-gray-200 text-xs leading-relaxed"
              >
                <p>
                  Provide your phone number to receive notifications about your account activity on
                  Athios. We will not share or sell your number.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div className="flex items-center space-x-2">
          <Input
            ref={phoneInputRef}
            placeholder="Phone number"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className={`${INPUT_CLASSES.dark} flex-1`}
            disabled={athlete.phone_verified || codeSent || isLoading}
            type="tel"
            autoComplete="tel"
          />

          {athlete.phone_verified ? (
            <>
              <span className="text-[#C0FD72] flex items-center px-2 font-medium whitespace-nowrap">
                ✓ Verified
              </span>
              <Button
                type="button"
                variant="ghost"
                className="text-gray-400 hover:text-red-700 whitespace-nowrap text-xs px-2"
                onClick={handleRemovePhone}
                disabled={isLoading}
              >
                {isLoading ? 'Removing...' : 'Remove'}
              </Button>
            </>
          ) : (
            !codeSent && (
              <Button
                type="button"
                variant="outline"
                className="border-[#C0FD72] text-[#C0FD72] hover:bg-[#C0FD72] hover:text-black whitespace-nowrap"
                onClick={handleSendCode}
                disabled={isLoading || !phone}
              >
                {isLoading ? 'Sending...' : 'Send Code'}
              </Button>
            )
          )}
        </div>
      </div>

      {!athlete.phone_verified && !codeSent && (
        <p className="text-xs text-gray-500 leading-relaxed">
          By adding your phone number and tapping <span className="text-gray-400">Send Code</span>,
          you agree to receive a one-time SMS verification code from Athios. Standard messaging
          rates may apply. You can remove your number at any time from this page.
        </p>
      )}

      {codeSent && !athlete.phone_verified && (
        <div className="flex flex-col space-y-2 animate-in fade-in slide-in-from-top-2">
          <FormLabel className="text-white">Verification Code</FormLabel>
          <div className="flex items-center space-x-2">
            <Input
              placeholder="Enter 6-digit code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className={`${INPUT_CLASSES.dark} flex-1`}
              maxLength={6}
              autoComplete="one-time-code"
              inputMode="numeric"
            />
            <Button
              type="button"
              variant="lime"
              className="whitespace-nowrap"
              onClick={handleVerifyCode}
              disabled={isLoading || !code}
            >
              {isLoading ? 'Verifying...' : 'Verify'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="text-gray-400 whitespace-nowrap"
              onClick={() => setCodeSent(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
          </div>
          <p className="text-xs text-gray-500">
            Check your messages for the 6-digit code. It may take a moment to arrive.
          </p>
        </div>
      )}
    </div>
  )
}

export default AccountSettings
